import { useState, useEffect, useMemo, useCallback } from 'react';
import { apiFetch } from '../api';

const RECEIPT_TABS = [
  { key: 'draft', label: 'Draft' },
  { key: 'final', label: 'Final' },
  { key: 'all', label: 'All' },
];

const CATEGORIES = [
  'Food & Dining',
  'Travel',
  'Office Supplies',
  'Software & Tech',
  'Utilities',
  'Medical',
  'Entertainment',
  'Shipping',
  'Advertising',
  'Other',
];

const EMPTY_FORM = {
  vendor: '',
  date: '',
  amount: '',
  category: '',
  description: '',
  comments: '',
};

function ReceiptRecordsPanel({ refreshKey, extraRefreshKey }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('draft');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Attachments modal
  const [attachmentsRecord, setAttachmentsRecord] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFilename, setPreviewFilename] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);

  // Finalize error
  const [finalizeError, setFinalizeError] = useState('');

  const fetchRecords = useCallback(async () => {
    try {
      const res = await apiFetch('/api/receipt-records');
      if (res.ok) {
        setRecords(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch receipt records:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [refreshKey, extraRefreshKey, fetchRecords]);

  const filtered = useMemo(() => {
    const sorted = [...records].sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date) - new Date(a.date);
    });
    if (activeTab === 'draft') return sorted.filter((r) => !r.finalized);
    if (activeTab === 'final') return sorted.filter((r) => r.finalized);
    return sorted;
  }, [records, activeTab]);

  const tabCounts = useMemo(() => ({
    draft: records.filter((r) => !r.finalized).length,
    final: records.filter((r) => r.finalized).length,
    all: records.length,
  }), [records]);

  function openAddModal() {
    setEditingRecord(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowModal(true);
  }

  function openEditModal(record) {
    setEditingRecord(record);
    setForm({
      vendor: record.vendor || '',
      date: record.date ? record.date.slice(0, 10) : '',
      amount: record.amount != null ? String(record.amount) : '',
      category: record.category || '',
      description: record.description || '',
      comments: record.comments || '',
    });
    setFormError('');
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditingRecord(null);
    setForm(EMPTY_FORM);
    setFormError('');
  }

  function handleFormChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setFormError('');

    const body = {
      vendor: form.vendor.trim() || null,
      date: form.date || null,
      amount: form.amount !== '' ? parseFloat(form.amount) : null,
      category: form.category || null,
      description: form.description.trim() || null,
      comments: form.comments.trim() || null,
    };

    try {
      let res;
      if (editingRecord) {
        res = await apiFetch(`/api/receipt-records/${editingRecord.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      } else {
        res = await apiFetch('/api/receipt-records', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
      }

      if (res.ok) {
        closeModal();
        await fetchRecords();
      } else {
        const data = await res.json().catch(() => ({}));
        setFormError(data.error || 'Failed to save receipt.');
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(record) {
    if (!window.confirm(`Delete receipt from "${record.vendor || 'Unknown'}"?`)) return;
    try {
      await apiFetch(`/api/receipt-records/${record.id}`, { method: 'DELETE' });
      await fetchRecords();
    } catch (err) {
      console.error('Failed to delete receipt:', err);
    }
  }

  async function handleFinalize(record) {
    setFinalizeError('');
    if (!record.date || record.amount == null || !record.category) {
      setFinalizeError(`Receipt #${record.id} needs date, amount, and category before finalizing.`);
      return;
    }
    try {
      const res = await apiFetch(`/api/receipt-records/${record.id}/finalize`, { method: 'POST' });
      if (res.ok) {
        await fetchRecords();
      } else {
        const data = await res.json().catch(() => ({}));
        setFinalizeError(data.error || 'Failed to finalize.');
      }
    } catch (err) {
      setFinalizeError(err.message);
    }
  }

  async function handleUnfinalize(record) {
    try {
      const res = await apiFetch(`/api/receipt-records/${record.id}/unfinalize`, { method: 'POST' });
      if (res.ok) {
        await fetchRecords();
      }
    } catch (err) {
      console.error('Failed to unfinalize:', err);
    }
  }

  async function openAttachments(record) {
    setAttachmentsRecord(record);
    setAttachments([]);
    setAttachmentsLoading(true);
    setPreviewUrl(null);
    setPreviewFilename(null);
    try {
      const res = await apiFetch(`/api/receipt-records/${record.id}/attachments`);
      if (res.ok) setAttachments(await res.json());
    } catch (err) {
      console.error('Failed to load attachments:', err);
    } finally {
      setAttachmentsLoading(false);
    }
  }

  function closeAttachments() {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setAttachmentsRecord(null);
    setAttachments([]);
    setPreviewUrl(null);
    setPreviewFilename(null);
  }

  async function previewAttachment(recordId, filename) {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewFilename(filename);
    setPreviewLoading(true);
    setPreviewUrl(null);
    try {
      const res = await apiFetch(`/api/receipt-records/${recordId}/attachments/${encodeURIComponent(filename)}`);
      if (res.ok) {
        const blob = await res.blob();
        setPreviewUrl(URL.createObjectURL(blob));
      }
    } catch (err) {
      console.error('Failed to load attachment:', err);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleUpload(record, file) {
    if (!file) return;
    setUploadingFor(record.id);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await apiFetch(`/api/receipt-records/${record.id}/attachments`, {
        method: 'POST',
        body: formData,
      });
      if (res.ok && attachmentsRecord && attachmentsRecord.id === record.id) {
        const updated = await apiFetch(`/api/receipt-records/${record.id}/attachments`);
        if (updated.ok) setAttachments(await updated.json());
      }
    } catch (err) {
      console.error('Failed to upload attachment:', err);
    } finally {
      setUploadingFor(null);
    }
  }

  function isImage(filename) {
    return /\.(png|jpg|jpeg|gif|webp)$/i.test(filename);
  }

  function isPdf(filename) {
    return /\.pdf$/i.test(filename);
  }

  function formatAmount(amount) {
    if (amount == null) return '—';
    return `$${parseFloat(amount).toFixed(2)}`;
  }

  function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' });
  }

  if (loading) {
    return (
      <section className="card">
        <h2>Receipts</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  return (
    <section className="card receipts-section">
      <div className="goals-header">
        <h2>Receipts</h2>
        <button className="manage-btn" onClick={openAddModal}>
          + Add Receipt
        </button>
      </div>

      {finalizeError && (
        <div className="rr-error-banner">{finalizeError}</div>
      )}

      <div className="receipts-tabs">
        {RECEIPT_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`receipts-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label} ({tabCounts[tab.key] || 0})
          </button>
        ))}
      </div>

      <div className="receipts-list">
        {filtered.length === 0 && (
          <div className="receipts-empty">No receipts in this category</div>
        )}
        {filtered.map((record) => (
          <div key={record.id} className="rr-item">
            <div className="rr-item-main">
              <div className="rr-item-top">
                <span className="rr-vendor">{record.vendor || <em className="rr-empty-field">No vendor</em>}</span>
                {record.finalized && <span className="rr-badge rr-badge-final">Final</span>}
                <span className="rr-amount">{formatAmount(record.amount)}</span>
              </div>
              <div className="rr-item-meta">
                <span className="rr-date">{formatDate(record.date)}</span>
                {record.category && <span className="rr-category">{record.category}</span>}
                {record.sourceInfo && (
                  <span className="rr-source">From: {record.sourceInfo}</span>
                )}
              </div>
              {record.description && (
                <div className="rr-description">{record.description}</div>
              )}
              {record.comments && (
                <div className="rr-comments">{record.comments}</div>
              )}
            </div>
            <div className="rr-item-actions">
              <button
                className="receipt-action-btn view-btn"
                onClick={() => openEditModal(record)}
                title="Edit receipt"
              >
                Edit
              </button>
              <button
                className="receipt-action-btn view-btn"
                onClick={() => openAttachments(record)}
                title="Attachments"
              >
                Files
              </button>
              <label
                className="receipt-action-btn view-btn rr-upload-label"
                title="Upload attachment"
              >
                {uploadingFor === record.id ? '...' : 'Upload'}
                <input
                  type="file"
                  style={{ display: 'none' }}
                  onChange={(e) => { handleUpload(record, e.target.files[0]); e.target.value = ''; }}
                  disabled={uploadingFor === record.id}
                />
              </label>
              {!record.finalized ? (
                <button
                  className="receipt-action-btn import-btn"
                  onClick={() => handleFinalize(record)}
                  title="Finalize receipt"
                >
                  Finalize
                </button>
              ) : (
                <button
                  className="receipt-action-btn review-btn"
                  onClick={() => handleUnfinalize(record)}
                  title="Move back to draft"
                >
                  Unfinalize
                </button>
              )}
              <button
                className="receipt-action-btn ignore-btn"
                onClick={() => handleDelete(record)}
                title="Delete receipt"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content rr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-preview-header">
              <h2>{editingRecord ? 'Edit Receipt' : 'Add Receipt'}</h2>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSave} className="rr-form">
              {formError && <div className="rr-error-banner">{formError}</div>}
              <div className="rr-form-row">
                <label className="rr-label">Vendor</label>
                <input
                  className="rr-input"
                  type="text"
                  name="vendor"
                  value={form.vendor}
                  onChange={handleFormChange}
                  placeholder="Vendor name"
                  autoFocus
                />
              </div>
              <div className="rr-form-row">
                <label className="rr-label">Date</label>
                <input
                  className="rr-input"
                  type="date"
                  name="date"
                  value={form.date}
                  onChange={handleFormChange}
                />
              </div>
              <div className="rr-form-row">
                <label className="rr-label">Amount ($)</label>
                <input
                  className="rr-input"
                  type="number"
                  name="amount"
                  value={form.amount}
                  onChange={handleFormChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div className="rr-form-row">
                <label className="rr-label">Category</label>
                <select
                  className="rr-input"
                  name="category"
                  value={form.category}
                  onChange={handleFormChange}
                >
                  <option value="">— Select —</option>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="rr-form-row">
                <label className="rr-label">Description</label>
                <input
                  className="rr-input"
                  type="text"
                  name="description"
                  value={form.description}
                  onChange={handleFormChange}
                  placeholder="Short description"
                />
              </div>
              <div className="rr-form-row">
                <label className="rr-label">Comments</label>
                <textarea
                  className="rr-input rr-textarea"
                  name="comments"
                  value={form.comments}
                  onChange={handleFormChange}
                  placeholder="Notes or comments..."
                  rows={3}
                />
              </div>
              <div className="rr-form-actions">
                <button type="button" className="receipt-action-btn ignore-btn" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="receipt-action-btn import-btn" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attachments Modal */}
      {attachmentsRecord && (
        <div className="modal-overlay" onClick={closeAttachments}>
          <div className="modal-content receipt-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="receipt-preview-header">
              <h2>Attachments — {attachmentsRecord.vendor || 'Receipt'}</h2>
              <button className="modal-close" onClick={closeAttachments}>&times;</button>
            </div>
            <div className="rr-attachments-body">
              {attachmentsLoading && <p className="loading">Loading...</p>}
              {!attachmentsLoading && attachments.length === 0 && (
                <p className="receipts-empty">No attachments yet.</p>
              )}
              {!attachmentsLoading && attachments.length > 0 && (
                <div className="receipt-preview-file-selector">
                  {attachments.map((fn) => (
                    <button
                      key={fn}
                      className={`receipt-file-tab${previewFilename === fn ? ' active' : ''}`}
                      onClick={() => previewAttachment(attachmentsRecord.id, fn)}
                    >
                      {fn}
                    </button>
                  ))}
                </div>
              )}
              <div className="receipt-preview-body">
                {previewLoading && <p className="loading">Loading preview...</p>}
                {!previewLoading && previewUrl && previewFilename && isPdf(previewFilename) && (
                  <iframe
                    src={previewUrl}
                    title={previewFilename}
                    className="receipt-preview-iframe"
                  />
                )}
                {!previewLoading && previewUrl && previewFilename && isImage(previewFilename) && (
                  <img
                    src={previewUrl}
                    alt={previewFilename}
                    className="receipt-preview-image"
                  />
                )}
                {!previewLoading && previewUrl && previewFilename && !isPdf(previewFilename) && !isImage(previewFilename) && (
                  <div className="receipt-preview-unsupported">
                    <a href={previewUrl} download={previewFilename} className="receipt-download-link">
                      Download {previewFilename}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default ReceiptRecordsPanel;
